import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import { withCommunityRequestDeadline, COMMUNITY_READ_TIMEOUT_MS, COMMUNITY_PUBLISH_TIMEOUT_MS } from "./community-request-deadline.ts";
import type { AuthTokenSource } from "./api.ts";
import {
  cloneStrategyRuleDSL,
  type StrategyRuleDSL,
  type StrategyRuleCondition,
  type StrategyRuleGroup,
  type StrategyRuleOperand,
  type StrategyRuleOperator,
} from "./strategy-builder.ts";
import type { BacktestFormState } from "./types.ts";
export {
  applyCommunityReproduction,
  resolveStrategyCommunityCapability,
} from "./strategy-community-contract.ts";

const MAX_RESPONSE_BYTES = 768_000;
const MAX_ITEMS = 100;
const SUPPORTED_DSL_CATALOG_VERSIONS = new Set(["2026-07-21", "2026-07-22"]);
const PUBLICATION_ID_PATTERN = /^pub_[A-Za-z0-9_-]{43}$/;
const CREATOR_ID_PATTERN = /^crt_[A-Za-z0-9_-]{32}$/;
const SHARE_ID_PATTERN = /^shr_[A-Za-z0-9_-]{43}$/;
const ATTRIBUTION_ID_PATTERN = /^att_[A-Za-z0-9_-]{43}$/;
const SAFE_RULE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/;
const URL_OR_EMAIL = /(?:https?:\/\/|www\.|\b[^\s@]+@[^\s@]+\.[^\s@]+\b)/i;
const IMPORTABLE_TIMEFRAMES = new Set(["5m", "15m", "30m", "1h", "4h", "1d"]);
const EXECUTION_MODELS = new Set(["spot", "futures"]);
const MARGIN_MODES = new Set(["isolated", "cross"]);
const TRADE_DIRECTIONS = new Set(["long", "short", "both"]);
const EXIT_MODES = new Set(["tp_sl_only", "signal_and_risk", "signal_only"]);
const REPRODUCTION_PERIODS = new Set(["1M", "3M", "6M", "1Y", "Custom"]);
const PUBLICATION_STATUSES = new Set(["pending_review", "listed", "archived", "hidden"]);
const PUBLIC_STATUSES = new Set(["listed"]);
const PUBLISH_RESPONSE_STATUSES = new Set(["pending_review", "listed"]);
const EVIDENCE_STATES = new Set([
  "unvalidated",
  "in_sample",
  "selection_passed",
  "locked_holdout_passed",
  "locked_holdout_failed",
  "insufficient_evidence",
  "unknown",
]);

export type CommunityFeedTab = "recommended" | "verified" | "new" | "saved";
export type CommunityDetailTab = "overview" | "rules" | "evidence" | "trades" | "versions";

export interface StrategyCommunityCapability {
  enabled: boolean;
  publishing: boolean;
  generatedPublishing: boolean;
  thanks: boolean;
  favorites: boolean;
  reporting: boolean;
  blocking: boolean;
  duplicate: boolean;
  evidenceAttestation: boolean;
  liveExecution: boolean;
  creatorPayouts: boolean;
  performanceVerified: boolean;
  publishingRequiresReview: boolean;
}

export interface StrategyCommunityFilters {
  tab: CommunityFeedTab;
  query?: string;
  market?: string;
  marketID?: string;
  timeframe?: string;
  evidence?: string;
  maxDrawdown?: number;
  sort?: string;
  cursor?: string;
  limit?: number;
}

export interface CommunityCreator {
  id: string;
  displayName: string;
}

export interface CommunityMetrics {
  netReturnPercent: number | null;
  maxDrawdownPercent: number | null;
  winRatePercent: number | null;
  sharpeRatio: number | null;
  profitFactor: number | null;
  totalTrades: number | null;
  netProfitAmount: number | null;
}

export interface CommunityEvidence {
  state: string;
  label?: string;
  configurationAttested: boolean;
  reproducible: boolean;
  outOfSample: boolean;
  serverRerunCount: number;
  independentRerunCount: number;
  evaluatedAt?: string;
  notes: string[];
}

export interface CommunityRule {
  title?: string;
  description: string;
}

export interface CommunityTrade {
  id: string;
  symbol?: string;
  side?: string;
  entryAt?: string;
  exitAt?: string;
  entryPrice: number | null;
  exitPrice: number | null;
  profitAmount: number | null;
  profitPercent: number | null;
  fees: number | null;
}

export interface CommunityVersion {
  id: string;
  version: string;
  createdAt?: string;
  state?: string;
  changeSummary?: string;
  metrics?: CommunityMetrics;
}

export interface CommunityReproductionAssumptions {
  commissionPercent: number;
  slippageBps: number;
  positionSizePercent: number;
  executionModel: "spot" | "futures";
  marginMode: "isolated" | "cross";
  leverage: number;
  maintenanceMarginPercent: number;
  fundingRatePercent: number;
  fundingIntervalHours: number;
  liquidationFeePercent: number;
  tradeDirection: "long" | "short" | "both";
  exitMode: "tp_sl_only" | "signal_and_risk" | "signal_only";
  stopLossPercent: number;
  takeProfitPercent: number;
  startDate: string;
  endDate: string;
}

export interface CommunityReproduction {
  provider?: string;
  market?: string;
  strategyDSL: StrategyRuleDSL;
  strategyLabel: string;
  symbol: string;
  timeframe: BacktestFormState["timeframe"];
  assumptions: CommunityReproductionAssumptions;
}

export interface CommunityPublication {
  id: string;
  title: string;
  summary?: string;
  creator: CommunityCreator;
  symbol: string;
  timeframe: string;
  executionModel?: string;
  version?: string;
  status: "pending_review" | "listed" | "archived" | "hidden";
  publishedAt?: string;
  expiresAt?: string;
  updatedAt?: string;
  testedStart?: string;
  testedEnd?: string;
  metrics: CommunityMetrics;
  evidence: CommunityEvidence;
  sparkline: number[];
  tags: string[];
  isFavorited: boolean;
  favoriteCount: number;
  duplicateCount: number;
  thanksCount: number;
  isThanked: boolean;
  thanksAvailable: boolean;
  serverSimulationInitialCapital?: number;
  rules: CommunityRule[];
  trades: CommunityTrade[];
  versions: CommunityVersion[];
  reproduction?: CommunityReproduction;
}

export interface CommunityFeedPage {
  items: CommunityPublication[];
  nextCursor?: string;
  marketID?: string;
  selectionTimeframe?: string;
  selectionMarketID?: string;
  matchCount?: number;
  matchComplete?: boolean;
}

export interface CommunityDuplicateReceipt {
  accepted: boolean;
  shareID?: string;
  attributionEventID?: string;
  reproduction?: CommunityReproduction;
}

export interface OwnedReproducibleShare {
  shareID: string;
  strategyLabel: string;
  symbol: string;
  timeframe: string;
  expiresAt?: string;
  revokedAt?: string;
  publicationID?: string;
  evidenceState?: string;
  generatedEligible: boolean;
  netReturnPercent: number | null;
}

type JsonRecord = Record<string, unknown>;

type IndicatorContract = {
  parameters: Record<string, readonly [minimum: number, maximum: number]>;
  operators: ReadonlySet<StrategyRuleOperator>;
  comparisonFamily: string;
  valueType: string;
  numericRange?: readonly [minimum: number, maximum: number];
};

const BINARY_OPERATORS = new Set<StrategyRuleOperator>([
  "gt", "gte", "lt", "lte", "crosses_above", "crosses_below",
]);
const ALL_OPERATORS = new Set<StrategyRuleOperator>([
  ...BINARY_OPERATORS, "rising", "falling",
]);
const UNARY_OPERATORS = new Set<StrategyRuleOperator>(["rising", "falling"]);

function indicatorContract(
  parameters: IndicatorContract["parameters"],
  comparisonFamily: string,
  valueType: string,
  operators: ReadonlySet<StrategyRuleOperator> = ALL_OPERATORS,
  numericRange?: IndicatorContract["numericRange"],
): IndicatorContract {
  return { parameters, operators, comparisonFamily, valueType, numericRange };
}

const INDICATOR_CONTRACTS: Readonly<Record<string, IndicatorContract>> = {
  open: indicatorContract({}, "price", "price", BINARY_OPERATORS),
  high: indicatorContract({}, "price", "price", BINARY_OPERATORS),
  low: indicatorContract({}, "price", "price", BINARY_OPERATORS),
  close: indicatorContract({}, "price", "price", BINARY_OPERATORS),
  volume: indicatorContract({}, "volume", "volume", BINARY_OPERATORS),
  sma: indicatorContract({ period: [2, 500] }, "price", "price"),
  ema: indicatorContract({ period: [2, 500] }, "price", "price"),
  rsi: indicatorContract({ period: [2, 100] }, "reflection_0_100", "oscillator", ALL_OPERATORS, [0, 100]),
  atr: indicatorContract({ period: [2, 100] }, "volatility", "price_delta"),
  volume_sma: indicatorContract({ period: [2, 500] }, "volume", "volume"),
  previous_high: indicatorContract({ lookback: [2, 500] }, "price", "price", BINARY_OPERATORS),
  previous_low: indicatorContract({ lookback: [2, 500] }, "price", "price", BINARY_OPERATORS),
  macd_histogram: indicatorContract(
    { fast: [2, 200], slow: [3, 500], signal: [2, 100] },
    "centered_momentum",
    "momentum",
  ),
  adx: indicatorContract({ period: [2, 100] }, "directional_movement", "oscillator", ALL_OPERATORS, [0, 100]),
  adx_plus_di: indicatorContract({ period: [2, 100] }, "directional_movement", "oscillator", ALL_OPERATORS, [0, 100]),
  adx_minus_di: indicatorContract({ period: [2, 100] }, "directional_movement", "oscillator", ALL_OPERATORS, [0, 100]),
  stochastic_k: indicatorContract(
    { k_period: [2, 100], smooth_k: [1, 20] },
    "reflection_0_100",
    "oscillator",
    ALL_OPERATORS,
    [0, 100],
  ),
  stochastic_d: indicatorContract(
    { k_period: [2, 100], smooth_k: [1, 20], d_period: [1, 20] },
    "reflection_0_100",
    "oscillator",
    ALL_OPERATORS,
    [0, 100],
  ),
  donchian_upper: indicatorContract({ period: [2, 500] }, "price", "price"),
  donchian_lower: indicatorContract({ period: [2, 500] }, "price", "price"),
  donchian_middle: indicatorContract({ period: [2, 500] }, "price", "price"),
};

function record(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as JsonRecord
    : null;
}

function firstRecord(source: JsonRecord | null, ...keys: string[]): JsonRecord | null {
  for (const key of keys) {
    const candidate = record(source?.[key]);
    if (candidate) return candidate;
  }
  return null;
}

function text(source: JsonRecord | null, keys: string[], maximum = 240): string | undefined {
  for (const key of keys) {
    const candidate = source?.[key];
    if (typeof candidate !== "string") continue;
    const normalized = Array.from(candidate.trim())
      .filter((character) => !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(character))
      .join("")
      .slice(0, maximum);
    if (normalized) return normalized;
  }
  return undefined;
}

function strictText(
  value: unknown,
  minimum: number,
  maximum: number,
  { allowURL = false }: { allowURL?: boolean } = {},
): string | undefined {
  if (typeof value !== "string" || value !== value.trim()) return undefined;
  const length = Array.from(value).length;
  if (length < minimum || length > maximum || /[\p{Cc}\p{Cf}\p{Cs}]/u.test(value)) return undefined;
  if (!allowURL && URL_OR_EMAIL.test(value)) return undefined;
  return value;
}

function exactIdentifier(value: unknown, pattern: RegExp): string | undefined {
  return typeof value === "string" && pattern.test(value) ? value : undefined;
}

function strictMarketValue(value: unknown): string | undefined {
  return strictText(value, 1, 32, { allowURL: true });
}

function finite(source: JsonRecord | null, keys: string[]): number | null {
  for (const key of keys) {
    const candidate = source?.[key];
    if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
  }
  return null;
}

function bool(source: JsonRecord | null, keys: string[], fallback = false): boolean {
  for (const key of keys) {
    if (typeof source?.[key] === "boolean") return source[key] as boolean;
  }
  return fallback;
}

function boundedCount(value: number | null): number {
  return value == null ? 0 : Math.max(0, Math.min(Math.round(value), 10_000_000));
}

function isoDate(value: string | undefined): string | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) {
    return undefined;
  }
  return Number.isFinite(Date.parse(value)) ? value : undefined;
}

function stringList(value: unknown, maximumItems = 12, maximumLength = 120): string[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, maximumItems).flatMap((item) => {
    if (typeof item !== "string") return [];
    const normalized = text({ item }, ["item"], maximumLength);
    return normalized ? [normalized] : [];
  });
}

function parseMetrics(source: JsonRecord | null, allowUnavailableProfitFactor = false): CommunityMetrics | null {
  if (!source) return null;
  const netReturnPercent = finite(source, ["total_return_percent"]);
  const maxDrawdownPercent = finite(source, ["max_drawdown_percent"]);
  const winRatePercent = finite(source, ["win_rate_percent"]);
  const profitFactor = finite(source, ["profit_factor"]);
  const totalTrades = finite(source, ["total_trades"]);
  if (
    netReturnPercent == null || netReturnPercent < -1_000_000_000 || netReturnPercent > 1_000_000_000
    || maxDrawdownPercent == null || maxDrawdownPercent < 0 || maxDrawdownPercent > 100
    || winRatePercent == null || winRatePercent < 0 || winRatePercent > 100
    || (profitFactor == null && !(allowUnavailableProfitFactor && source.profit_factor == null))
    || (profitFactor != null && (profitFactor < 0 || profitFactor > 1_000_000_000))
    || totalTrades == null || !Number.isInteger(totalTrades) || totalTrades <= 0 || totalTrades > 10_000_000
  ) return null;
  const sharpeRatio = finite(source, ["sharpe_ratio"]);
  const netProfitAmount = finite(source, ["net_profit_amount"]);
  if (
    (sharpeRatio != null && (sharpeRatio < -1_000_000 || sharpeRatio > 1_000_000))
    || (netProfitAmount != null && Math.abs(netProfitAmount) > 1_000_000_000_000_000)
  ) return null;
  return {
    netReturnPercent,
    maxDrawdownPercent,
    winRatePercent,
    sharpeRatio,
    profitFactor,
    totalTrades,
    netProfitAmount,
  };
}

function parseEvidence(source: JsonRecord | null): CommunityEvidence | null {
  const evidence = record(source?.evidence);
  const attestation = record(evidence?.attestation);
  const checks = record(attestation?.checks);
  const state = typeof evidence?.state === "string" && EVIDENCE_STATES.has(evidence.state)
    ? evidence.state
    : undefined;
  const riskNote = strictText(evidence?.risk_note, 1, 1_000);
  const issuedAt = isoDate(typeof attestation?.issued_at === "string" ? attestation.issued_at : undefined);
  const contractAttested = attestation?.type === "reproducibility_contract_v1"
    && checks?.dsl_schema_valid === true
    && checks?.strategy_identity_match === true
    && checks?.tested_period_complete === true
    && checks?.metrics_well_formed === true
    && attestation?.performance_verified === false;
  if (!state || !riskNote || !issuedAt || !contractAttested) return null;
  return {
    state,
    // This is deliberately configuration-only. It must never be presented as
    // an attestation of profitability or future performance.
    configurationAttested: true,
    reproducible: true,
    outOfSample: state === "locked_holdout_passed" || state === "locked_holdout_failed",
    serverRerunCount: 0,
    independentRerunCount: 0,
    evaluatedAt: issuedAt,
    notes: [riskNote],
  };
}

function parseSparkline(source: JsonRecord | null): number[] | null {
  const candidate = source?.equity_curve;
  if (!Array.isArray(candidate) || candidate.length < 2 || candidate.length > 48) return null;
  const values: number[] = [];
  let previousProgress = -1;
  for (const point of candidate) {
    const item = record(point);
    const progress = finite(item, ["progress"]);
    const returnPercent = finite(item, ["return_percent"]);
    if (
      progress == null || progress < 0 || progress > 1
      || progress <= previousProgress
      || returnPercent == null || returnPercent < -1_000_000_000 || returnPercent > 1_000_000_000
    ) return null;
    previousProgress = progress;
    values.push(returnPercent);
  }
  const first = record(candidate[0]);
  const last = record(candidate[candidate.length - 1]);
  if (first?.progress !== 0 || first.return_percent !== 0 || last?.progress !== 1) return null;
  return values;
}

function parseIndicatorOperand(value: unknown): {
  operand: Extract<StrategyRuleOperand, { kind: "indicator" }>;
  contract: IndicatorContract;
} | null {
  const source = record(value);
  const id = typeof source?.id === "string" ? source.id : "";
  const contract = INDICATOR_CONTRACTS[id];
  const parameters = record(source?.parameters);
  if (source?.kind !== "indicator" || !contract || !parameters) return null;
  const expectedParameters = Object.keys(contract.parameters).sort();
  if (Object.keys(parameters).sort().join("\u0000") !== expectedParameters.join("\u0000")) return null;
  const parsedParameters: Record<string, number> = {};
  for (const parameter of expectedParameters) {
    const value = parameters[parameter];
    const [minimum, maximum] = contract.parameters[parameter];
    if (!Number.isInteger(value) || Number(value) < minimum || Number(value) > maximum) return null;
    parsedParameters[parameter] = Number(value);
  }
  if (
    id === "macd_histogram"
    && parsedParameters.fast >= parsedParameters.slow
  ) return null;
  return { operand: { kind: "indicator", id, parameters: parsedParameters }, contract };
}

function parseRuleOperand(value: unknown): {
  operand: StrategyRuleOperand;
  contract?: IndicatorContract;
} | null {
  const source = record(value);
  if (source?.kind === "number") {
    return typeof source.value === "number" && Number.isFinite(source.value)
      ? { operand: { kind: "number", value: source.value } }
      : null;
  }
  return parseIndicatorOperand(value);
}

function parseRuleCondition(value: unknown, seen: Set<string>): StrategyRuleCondition | null {
  const source = record(value);
  const id = typeof source?.id === "string" && SAFE_RULE_ID.test(source.id) ? source.id : undefined;
  const operator = typeof source?.operator === "string" && ALL_OPERATORS.has(source.operator as StrategyRuleOperator)
    ? source.operator as StrategyRuleOperator
    : undefined;
  const left = parseIndicatorOperand(source?.left);
  if (!id || seen.has(id) || !operator || !left || !left.contract.operators.has(operator)) return null;
  seen.add(id);
  if (UNARY_OPERATORS.has(operator)) {
    if (source && Object.prototype.hasOwnProperty.call(source, "right")) return null;
    return { id, left: left.operand, operator };
  }
  const right = parseRuleOperand(source?.right);
  if (!right) return null;
  if (right.operand.kind === "indicator") {
    if (
      right.contract?.comparisonFamily !== left.contract.comparisonFamily
      || right.contract?.valueType !== left.contract.valueType
      || JSON.stringify(right.operand) === JSON.stringify(left.operand)
    ) return null;
  } else if (
    left.contract.numericRange
    && (right.operand.value < left.contract.numericRange[0]
      || right.operand.value > left.contract.numericRange[1])
  ) return null;
  return { id, left: left.operand, operator, right: right.operand };
}

function parseRuleGroup(
  value: unknown,
  id: "entry" | "exit",
  seen: Set<string>,
): StrategyRuleGroup | null {
  const source = record(value);
  if (
    source?.id !== id
    || (source.match !== "all" && source.match !== "any")
    || !Array.isArray(source.conditions)
    || source.conditions.length > 5
    || seen.has(id)
  ) return null;
  seen.add(id);
  const conditions: StrategyRuleCondition[] = [];
  for (const condition of source.conditions) {
    const parsed = parseRuleCondition(condition, seen);
    if (!parsed) return null;
    conditions.push(parsed);
  }
  return { id, match: source.match, conditions };
}

function safeStrategyDSL(value: unknown): StrategyRuleDSL | undefined {
  const source = record(value);
  const catalogVersion = typeof source?.catalog_version === "string"
    && SUPPORTED_DSL_CATALOG_VERSIONS.has(source.catalog_version)
    ? source.catalog_version
    : undefined;
  if (
    source?.schema_version !== 2
    || !catalogVersion
    || source.short_mode !== "mirror_long"
    || record(source.risk)?.mode !== "inherit"
  ) return undefined;
  const seen = new Set<string>();
  const entry = parseRuleGroup(source.entry, "entry", seen);
  const exit = parseRuleGroup(source.exit, "exit", seen);
  if (!entry?.conditions.length || !exit || entry.conditions.length + exit.conditions.length > 10) {
    return undefined;
  }
  return cloneStrategyRuleDSL({
    schema_version: 2,
    catalog_version: catalogVersion,
    short_mode: "mirror_long",
    entry,
    exit,
    risk: { mode: "inherit" },
  });
}

function parseReproduction(source: JsonRecord | null): CommunityReproduction | undefined {
  const wrapper = record(source?.reproduction);
  if (!wrapper || wrapper.schema_version !== 1) return undefined;
  if ((wrapper.provider != null && !["auto", "binance", "binance_futures", "yfinance", "yahoo"].includes(String(wrapper.provider)))
    || (wrapper.market != null && !["auto", "crypto", "crypto_futures", "forex", "futures", "metal", "equity", "index", "futures_index"].includes(String(wrapper.market)))) return undefined;
  const assumptions = record(wrapper.assumptions);
  const strategyDSL = safeStrategyDSL(wrapper.strategy_dsl);
  const strategyLabel = strictText(wrapper.strategy_label, 1, 72);
  const symbol = strictMarketValue(wrapper.symbol);
  const timeframe = typeof wrapper.timeframe === "string" && IMPORTABLE_TIMEFRAMES.has(wrapper.timeframe)
    ? wrapper.timeframe
    : undefined;
  const executionModel = typeof assumptions?.execution_model === "string" ? assumptions.execution_model : undefined;
  const marginMode = typeof assumptions?.margin_mode === "string" ? assumptions.margin_mode : undefined;
  const tradeDirection = typeof assumptions?.trade_direction === "string" ? assumptions.trade_direction : undefined;
  const exitMode = typeof assumptions?.exit_mode === "string" ? assumptions.exit_mode : undefined;
  const period = typeof assumptions?.period === "string" ? assumptions.period : undefined;
  const startDate = isoDate(typeof assumptions?.start_date === "string" ? assumptions.start_date : undefined);
  const endDate = isoDate(typeof assumptions?.end_date === "string" ? assumptions.end_date : undefined);
  const numbers = {
    commissionPercent: finite(assumptions, ["commission_percent"]),
    slippageBps: finite(assumptions, ["slippage_bps"]),
    positionSizePercent: finite(assumptions, ["position_size_percent"]),
    leverage: finite(assumptions, ["leverage"]),
    maintenanceMarginPercent: finite(assumptions, ["maintenance_margin_percent"]),
    fundingRatePercent: finite(assumptions, ["funding_rate_percent"]),
    fundingIntervalHours: finite(assumptions, ["funding_interval_hours"]),
    liquidationFeePercent: finite(assumptions, ["liquidation_fee_percent"]),
    stopLossPercent: finite(assumptions, ["stop_loss_percent"]),
    takeProfitPercent: finite(assumptions, ["take_profit_percent"]),
  };
  if (
    !strategyDSL || !strategyLabel || !symbol || !timeframe
    || !executionModel || !EXECUTION_MODELS.has(executionModel)
    || !marginMode || !MARGIN_MODES.has(marginMode)
    || !tradeDirection || !TRADE_DIRECTIONS.has(tradeDirection)
    || !exitMode || !EXIT_MODES.has(exitMode)
    || !period || !REPRODUCTION_PERIODS.has(period)
    || !startDate || !endDate || Date.parse(startDate) >= Date.parse(endDate)
    || Object.values(numbers).some((value) => value == null)
  ) return undefined;
  const bounded = numbers as Record<keyof typeof numbers, number>;
  if (
    bounded.commissionPercent < 0 || bounded.commissionPercent > 5
    || bounded.slippageBps < 0 || bounded.slippageBps > 500
    || bounded.positionSizePercent <= 0 || bounded.positionSizePercent > 100
    || bounded.leverage < 1 || bounded.leverage > 125
    || bounded.maintenanceMarginPercent < 0 || bounded.maintenanceMarginPercent >= 100
    || bounded.fundingRatePercent <= -100 || bounded.fundingRatePercent >= 100
    || !Number.isInteger(bounded.fundingIntervalHours)
    || bounded.fundingIntervalHours < 1 || bounded.fundingIntervalHours > 168
    || bounded.liquidationFeePercent < 0 || bounded.liquidationFeePercent >= 100
    || bounded.stopLossPercent <= 0 || bounded.stopLossPercent > 50
    || bounded.takeProfitPercent <= 0 || bounded.takeProfitPercent > 100
    || (executionModel === "spot"
      && (bounded.leverage !== 1 || bounded.fundingRatePercent !== 0))
    || (executionModel === "futures"
      && bounded.maintenanceMarginPercent >= 100 / bounded.leverage)
  ) return undefined;
  return {
    strategyDSL,
    strategyLabel,
    symbol,
    ...(wrapper.provider != null ? { provider: wrapper.provider as string } : {}),
    ...(wrapper.market != null ? { market: wrapper.market as string } : {}),
    timeframe: timeframe as BacktestFormState["timeframe"],
    assumptions: {
      ...bounded,
      executionModel: executionModel as "spot" | "futures",
      marginMode: marginMode as "isolated" | "cross",
      tradeDirection: tradeDirection as "long" | "short" | "both",
      exitMode: exitMode as "tp_sl_only" | "signal_and_risk" | "signal_only",
      startDate,
      endDate,
    },
  };
}

function strictCount(source: JsonRecord | null, key: string): number | null {
  const value = finite(source, [key]);
  return value != null && Number.isInteger(value) && value >= 0 && value <= 10_000_000
    ? value
    : null;
}

function strictTags(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > 8) return null;
  const tags: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const tag = strictText(item, 1, 24);
    if (!tag || !/^[\p{L}\p{N}_][\p{L}\p{N}_ .+#/-]{0,23}$/u.test(tag)) return null;
    const folded = tag.toLocaleLowerCase("en");
    if (seen.has(folded)) return null;
    seen.add(folded);
    tags.push(tag);
  }
  return tags;
}

function parseCommunityPublicationForStatuses(
  value: unknown,
  allowedStatuses: ReadonlySet<string>,
): CommunityPublication | null {
  const source = record(value);
  if (!source || source.schema_version !== 1) return null;
  const id = exactIdentifier(source.id, PUBLICATION_ID_PATTERN);
  const status = typeof source.status === "string"
    && PUBLICATION_STATUSES.has(source.status)
    && allowedStatuses.has(source.status)
    ? source.status as CommunityPublication["status"]
    : undefined;
  const title = strictText(source.title, 1, 80);
  const summary = strictText(source.summary, 1, 500);
  const creator = record(source.creator);
  const creatorID = exactIdentifier(creator?.id, CREATOR_ID_PATTERN);
  const creatorAlias = strictText(creator?.alias, 2, 40);
  const market = record(source.market);
  const symbol = strictMarketValue(market?.symbol);
  const timeframe = typeof market?.timeframe === "string" && IMPORTABLE_TIMEFRAMES.has(market.timeframe)
    ? market.timeframe
    : undefined;
  const executionModel = typeof market?.execution_model === "string" && EXECUTION_MODELS.has(market.execution_model)
    ? market.execution_model
    : undefined;
  const evidenceRecord = record(source.evidence);
  const testedPeriod = record(evidenceRecord?.tested_period);
  const testedStart = isoDate(typeof testedPeriod?.start === "string" ? testedPeriod.start : undefined);
  const testedEnd = isoDate(typeof testedPeriod?.end === "string" ? testedPeriod.end : undefined);
  const createdAt = isoDate(typeof source.created_at === "string" ? source.created_at : undefined);
  const publishedAt = source.published_at == null
    ? undefined
    : isoDate(typeof source.published_at === "string" ? source.published_at : undefined);
  const expiresAt = isoDate(typeof source.expires_at === "string" ? source.expires_at : undefined);
  const directives = record(source.directives);
  const social = record(source.engagement);
  const favoriteCount = strictCount(social, "favorites");
  const duplicateCount = strictCount(social, "duplicate_started");
  const rerunCompleted = strictCount(social, "rerun_completed");
  const thanksCount = social?.thanks_count == null ? 0 : strictCount(social, "thanks_count");
  const isThanked = social?.is_thanked == null ? false : social.is_thanked;
  const serverSimulation = record(source.server_simulation);
  const serverSimulationInitialCapital = serverSimulation?.scope === "server_simulation_only"
    && typeof serverSimulation.initial_capital === "number"
    && Number.isFinite(serverSimulation.initial_capital) && serverSimulation.initial_capital > 0
    ? serverSimulation.initial_capital : undefined;
  const tags = strictTags(source.tags);
  const metrics = parseMetrics(record(evidenceRecord?.metrics), source.generated_content === true);
  const evidence = parseEvidence(source);
  const sparkline = parseSparkline(evidenceRecord);
  const reproduction = parseReproduction(source);
  if (
    !id || !status || !title || !summary || !creatorID || !creatorAlias
    || !symbol || !timeframe || !executionModel || !testedStart || !testedEnd
    || Date.parse(testedStart) >= Date.parse(testedEnd)
    || !createdAt || !expiresAt || Date.parse(expiresAt) <= Date.now()
    || (status === "listed" && !publishedAt)
    || directives?.live_execution !== false
    || directives?.creator_payouts !== false
    || directives?.performance_verified !== false
    || favoriteCount == null || duplicateCount == null || rerunCompleted == null
    || thanksCount == null || typeof isThanked !== "boolean"
    || typeof social?.is_favorited !== "boolean"
    || !tags || !metrics || !evidence || !sparkline || !reproduction
    || reproduction.symbol !== symbol
    || reproduction.timeframe !== timeframe
    || reproduction.assumptions.executionModel !== executionModel
    || Date.parse(reproduction.assumptions.startDate) !== Date.parse(testedStart)
    || Date.parse(reproduction.assumptions.endDate) !== Date.parse(testedEnd)
  ) return null;
  evidence.independentRerunCount = rerunCompleted;
  return {
    id,
    title,
    summary,
    creator: {
      id: creatorID,
      displayName: creatorAlias,
    },
    symbol,
    timeframe,
    executionModel,
    status,
    publishedAt,
    expiresAt,
    testedStart,
    testedEnd,
    metrics,
    evidence,
    sparkline,
    tags,
    isFavorited: social.is_favorited,
    favoriteCount,
    duplicateCount,
    thanksCount,
    isThanked,
    thanksAvailable: social.thanks_available !== false,
    serverSimulationInitialCapital,
    rules: [],
    // Community v1 deliberately excludes raw trades and private result history.
    trades: [],
    versions: [],
    reproduction,
  };
}

export function parseCommunityPublication(value: unknown): CommunityPublication | null {
  return parseCommunityPublicationForStatuses(value, PUBLIC_STATUSES);
}

export function parseCommunityFeed(value: unknown): CommunityFeedPage {
  const envelope = record(value);
  const rawItems = envelope?.items;
  if (envelope?.schema_version !== 1 || !Array.isArray(rawItems) || rawItems.length > MAX_ITEMS) {
    return { items: [] };
  }
  const items = rawItems
    .flatMap((item) => {
        const parsed = parseCommunityPublication(item);
        return parsed ? [parsed] : [];
      });
  return {
    items,
    marketID: typeof envelope.market_id === "string" ? envelope.market_id : undefined,
    selectionMarketID: typeof record(envelope.selection)?.market_id === "string" ? record(envelope.selection)!.market_id as string : undefined,
    matchCount: Number.isSafeInteger(envelope.match_count) && Number(envelope.match_count) >= 0 ? envelope.match_count as number : undefined,
    selectionTimeframe: typeof record(envelope.selection)?.timeframe === "string" ? record(envelope.selection)!.timeframe as string : undefined,
    // Invalid/dropped items cannot prove that the requested market is empty.
    matchComplete: typeof envelope.match_complete === "boolean" && (!envelope.match_complete || envelope.has_more === false) && items.length === rawItems.length
      && envelope.match_count === rawItems.length && (envelope.next_cursor == null || typeof envelope.next_cursor === "string" && Boolean(strictText(envelope.next_cursor, 1, 512, { allowURL: true }))) ? envelope.match_complete : undefined,
    nextCursor: envelope.next_cursor == null
      ? undefined
      : strictText(envelope.next_cursor, 1, 512, { allowURL: true }),
  };
}

async function resolvedToken(source: AuthTokenSource): Promise<string> {
  const value = typeof source === "function" ? await source() : source;
  if (!value) throw new Error("community_sign_in_required");
  return value;
}

async function boundedJSON(response: Response): Promise<unknown> {
  const declared = Number(response.headers.get("content-length"));
  if (Number.isFinite(declared) && declared > MAX_RESPONSE_BYTES) throw new Error("community_response_too_large");
  if (!response.body) throw new Error("community_response_invalid");
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  while (true) {
    const item = await reader.read();
    if (item.done) break;
    size += item.value.byteLength;
    if (size > MAX_RESPONSE_BYTES) {
      await reader.cancel("community_response_too_large");
      throw new Error("community_response_too_large");
    }
    chunks.push(item.value);
  }
  if (size === 0) return {};
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  try {
    return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  } catch {
    throw new Error("community_response_invalid");
  }
}

async function communityRequest(
  path: string,
  init: RequestInit,
  locale: string,
  authToken?: AuthTokenSource | null,
  isCurrent?: () => boolean,
): Promise<unknown> {
  const publishing = init.method === "POST" && (path === "/api/v2/strategy-publications/generated" || path === "/api/v2/strategy-publications");
  return withCommunityRequestDeadline(publishing ? COMMUNITY_PUBLISH_TIMEOUT_MS : COMMUNITY_READ_TIMEOUT_MS, init.signal, async (signal) => {
    init = { ...init, signal };
    const ensureCurrent = () => {
      if (init.signal?.aborted || (isCurrent && !isCurrent())) {
        throw new DOMException("Community request is no longer current.", "AbortError");
      }
    };
    ensureCurrent();
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    headers.set("Accept-Language", locale || "en");
    if (init.body) headers.set("Content-Type", "application/json");
    if (authToken) headers.set("Authorization", `Bearer ${await resolvedToken(authToken)}`);
    // A Firebase token can resolve after sign-out, before React unmounts the
    // dialog. Never send the old account's mutation after that live fence moves.
    ensureCurrent();
    const response = await browserSafeFetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
    });
    ensureCurrent();
    if (!response.ok) {
      const value = await boundedJSON(response).catch(() => null);
      const source = record(value);
      const detail = record(source?.detail);
      const reason = text(detail, ["reason", "message"], 160) || text(source, ["reason", "message", "detail"], 160);
      const error = new Error(reason || `community_request_failed_${response.status}`) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    const value = await boundedJSON(response);
    ensureCurrent();
    return value;
  });
}

export async function fetchCommunityFeed(
  filters: StrategyCommunityFilters,
  locale: string,
  authToken?: AuthTokenSource | null,
  signal?: AbortSignal,
): Promise<CommunityFeedPage> {
  const params = new URLSearchParams();
  params.set("sort", filters.sort || (filters.tab === "new" ? "newest" : "evidence_risk_freshness"));
  params.set("limit", String(Math.max(1, Math.min(filters.limit || 24, 50))));
  if (filters.query?.trim()) params.set("query", filters.query.trim().slice(0, 100));
  if (filters.marketID) params.set("market_id", filters.marketID);
  else if (filters.market?.trim()) params.set("symbol", filters.market.trim().slice(0, 32));
  if (filters.timeframe?.trim()) params.set("timeframe", filters.timeframe.trim().slice(0, 16));
  if (filters.evidence?.trim()) params.set("evidence_state", filters.evidence.trim().slice(0, 48));
  if (typeof filters.maxDrawdown === "number" && Number.isFinite(filters.maxDrawdown)) {
    params.set("max_drawdown_percent", String(Math.max(0, Math.min(filters.maxDrawdown, 100))));
  }
  if (filters.cursor) params.set("cursor", filters.cursor.slice(0, 512));
  const path = filters.tab === "saved"
    ? "/api/v2/strategy-publications/favorites"
    : "/api/v2/strategy-publications";
  const page = parseCommunityFeed(await communityRequest(
    `${path}?${params.toString()}`,
    { method: "GET", signal },
    locale,
    authToken,
  ));
  if (filters.marketID && (page.marketID !== filters.marketID || page.selectionMarketID !== filters.marketID
    || page.selectionTimeframe !== filters.timeframe || page.matchComplete == null || page.matchCount == null)) {
    throw new Error("community_market_match_unconfirmed");
  }
  return page;
}

export async function fetchCommunityPublication(
  publicationID: string,
  locale: string,
  authToken?: AuthTokenSource | null,
  signal?: AbortSignal,
): Promise<CommunityPublication> {
  if (!PUBLICATION_ID_PATTERN.test(publicationID)) throw new Error("community_publication_invalid");
  const value = await communityRequest(
    `/api/v2/strategy-publications/${encodeURIComponent(publicationID)}`,
    { method: "GET", signal },
    locale,
    authToken,
  );
  const envelope = record(value);
  const publication = parseCommunityPublication(
    envelope?.strategy_publication || envelope?.publication || value,
  );
  if (!publication || publication.id !== publicationID) throw new Error("community_publication_invalid");
  return publication;
}

function idempotencyKey(prefix: string): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? `${prefix}-${crypto.randomUUID()}`
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function publicationAction(
  publicationID: string,
  path: "favorite" | "reports" | "duplicate-attributions" | "thanks",
  method: "POST" | "DELETE",
  body: JsonRecord,
  locale: string,
  authToken?: AuthTokenSource | null,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<unknown> {
  if (!PUBLICATION_ID_PATTERN.test(publicationID)) throw new Error("community_publication_invalid");
  return communityRequest(
    `/api/v2/strategy-publications/${encodeURIComponent(publicationID)}/${path}`,
    {
      method,
      signal,
      headers: { "X-Idempotency-Key": idempotencyKey(`community-${path}`) },
      body: JSON.stringify(body),
    },
    locale,
    authToken,
    isCurrent,
  );
}

export async function setCommunityThanks(
  publicationID: string,
  thanked: boolean,
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<{ publicationID: string; thanksCount: number; isThanked: boolean }> {
  const value = record(await publicationAction(
    publicationID, "thanks", thanked ? "POST" : "DELETE", {}, locale, authToken, signal, isCurrent,
  ));
  const thanksCount = strictCount(value, "thanks_count");
  if (value?.publication_id !== publicationID || thanksCount == null || typeof value.is_thanked !== "boolean") {
    throw new Error("community_thanks_receipt_invalid");
  }
  return { publicationID, thanksCount, isThanked: value.is_thanked };
}

export async function setCommunityFavorite(
  publicationID: string,
  favorite: boolean,
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<void> {
  await publicationAction(publicationID, "favorite", favorite ? "POST" : "DELETE", {}, locale, authToken, signal, isCurrent);
}

export async function blockCommunityCreator(
  creatorID: string,
  blocked: boolean,
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<void> {
  if (!CREATOR_ID_PATTERN.test(creatorID)) throw new Error("community_creator_invalid");
  await communityRequest(
    `/api/v2/strategy-publications/creators/${encodeURIComponent(creatorID)}/block`,
    {
      method: blocked ? "POST" : "DELETE",
      signal,
      headers: { "X-Idempotency-Key": idempotencyKey("community-block-creator") },
      body: JSON.stringify({}),
    },
    locale,
    authToken,
    isCurrent,
  );
}

export async function reportCommunityPublication(
  publicationID: string,
  reason: string,
  details: string,
  locale: string,
  authToken?: AuthTokenSource | null,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<void> {
  if (!["misleading", "spam", "unsafe", "other"].includes(reason)) {
    throw new Error("community_report_reason_invalid");
  }
  await publicationAction(publicationID, "reports", "POST", {
    reason: reason.slice(0, 48),
    details: details.trim().slice(0, 500),
  }, locale, authToken, signal, isCurrent);
}

export async function duplicateCommunityPublication(
  publication: CommunityPublication,
  locale: string,
  authToken?: AuthTokenSource | null,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<CommunityDuplicateReceipt> {
  const value = await publicationAction(publication.id, "duplicate-attributions", "POST", {
    event: "duplicate_started",
    client_event_id: idempotencyKey("community-duplicate-event"),
  }, locale, authToken, signal, isCurrent);
  const envelope = record(value);
  const shareID = exactIdentifier(envelope?.source_share_id, SHARE_ID_PATTERN);
  const attributionEventID = exactIdentifier(envelope?.attribution_event_id, ATTRIBUTION_ID_PATTERN);
  if (
    envelope?.accepted !== true
    || envelope.publication_id !== publication.id
    || !shareID
    || !attributionEventID
    || !publication.reproduction
  ) throw new Error("community_duplicate_receipt_invalid");
  return {
    accepted: true,
    shareID,
    attributionEventID,
    // The attribution endpoint binds accounting only. It may never replace the
    // immutable recipe already validated from the listed publication.
    reproduction: publication.reproduction,
  };
}

export function parseOwnedReproducibleShares(value: unknown): OwnedReproducibleShare[] {
  const envelope = record(value);
  const rawItems = envelope?.items;
  if (envelope?.schema_version !== 1 || !Array.isArray(rawItems) || rawItems.length > MAX_ITEMS) return [];
  return rawItems.flatMap((item) => {
    const source = record(item);
    const shareID = exactIdentifier(source?.share_id, SHARE_ID_PATTERN);
    const strategyLabel = strictText(source?.strategy_label, 1, 72);
    const symbol = strictMarketValue(source?.symbol);
    const timeframe = typeof source?.timeframe === "string" && IMPORTABLE_TIMEFRAMES.has(source.timeframe)
      ? source.timeframe
      : undefined;
    const executionModel = typeof source?.execution_model === "string" && EXECUTION_MODELS.has(source.execution_model)
      ? source.execution_model
      : undefined;
    const expiresAt = isoDate(typeof source?.expires_at === "string" ? source.expires_at : undefined);
    const evidenceState = typeof source?.evidence_state === "string" && EVIDENCE_STATES.has(source.evidence_state)
      ? source.evidence_state
      : undefined;
    if (
      !shareID || !strategyLabel || !symbol || !timeframe || !executionModel
      || !expiresAt || Date.parse(expiresAt) <= Date.now() || !evidenceState
    ) return [];
    return [{
      shareID,
      strategyLabel,
      symbol,
      timeframe,
      expiresAt,
      revokedAt: undefined,
      publicationID: undefined,
      evidenceState,
      generatedEligible: source?.generated_eligible === true,
      netReturnPercent: typeof source?.net_return_percent === "number" && Number.isFinite(source.net_return_percent)
        ? source.net_return_percent : null,
    }];
  });
}

export async function fetchOwnedReproducibleShares(
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
  includeGeneratedPending = false,
): Promise<OwnedReproducibleShare[]> {
  return parseOwnedReproducibleShares(await communityRequest(
    `/api/v2/strategy-publications/publishable-sources${includeGeneratedPending ? "?include_generated_pending=true" : ""}`,
    { method: "GET", signal },
    locale,
    authToken,
    isCurrent,
  ));
}

export interface OwnedCommunityPublication {
  id: string; title: string; status: CommunityPublication["status"]; symbol: string; timeframe: string;
  createdAt: string; publishedAt?: string; expiresAt: string; netReturnPercent: number; canOpen: boolean;
}
export interface OwnedCommunityPublicationsPage { items: OwnedCommunityPublication[]; nextCursor?: string }
/** Owner history is separate from the public feed, including pending/hidden/archived records. */
export function parseOwnedCommunityPublications(value: unknown): OwnedCommunityPublicationsPage {
  const envelope = record(value);
  if (envelope?.schema_version !== 1 || !Array.isArray(envelope.items) || envelope.items.length > 50
    || typeof envelope.has_more !== "boolean" || (envelope.next_cursor != null && (typeof envelope.next_cursor !== "string" || !envelope.next_cursor || envelope.next_cursor.length > 512))
    || envelope.has_more !== Boolean(envelope.next_cursor)) throw new Error("community_owner_history_invalid");
  const ids = new Set<string>();
  const items = envelope.items.map((item): OwnedCommunityPublication => {
    const source = record(item), market = record(source?.market), evidence = record(source?.evidence), metrics = record(evidence?.metrics);
    const id = exactIdentifier(source?.id, PUBLICATION_ID_PATTERN), title = strictText(source?.title, 1, 80);
    const symbol = strictMarketValue(market?.symbol), timeframe = typeof market?.timeframe === "string" && IMPORTABLE_TIMEFRAMES.has(market.timeframe) ? market.timeframe : undefined;
    const createdAt = isoDate(typeof source?.created_at === "string" ? source.created_at : undefined);
    const expiresAt = isoDate(typeof source?.expires_at === "string" ? source.expires_at : undefined);
    const publishedAt = source?.published_at == null ? undefined : isoDate(typeof source.published_at === "string" ? source.published_at : undefined);
    const netReturnPercent = metrics?.total_return_percent;
    if (!id || ids.has(id) || !title || !symbol || !timeframe || !createdAt || !expiresAt || source?.schema_version !== 1
      || typeof source.status !== "string" || !PUBLICATION_STATUSES.has(source.status)
      || typeof netReturnPercent !== "number" || !Number.isFinite(netReturnPercent)
      || source.status === "listed" && !publishedAt) throw new Error("community_owner_history_invalid");
    ids.add(id);
    return { id, title, symbol, timeframe, status: source.status as CommunityPublication["status"], createdAt, publishedAt, expiresAt,
      netReturnPercent, canOpen: source.status === "listed" && Date.parse(expiresAt) > Date.now() };
  });
  return { items, nextCursor: envelope.next_cursor as string | undefined || undefined };
}
export async function fetchOwnedCommunityPublications(locale: string, authToken: AuthTokenSource, signal?: AbortSignal, isCurrent?: () => boolean, cursor?: string): Promise<OwnedCommunityPublicationsPage> {
  const params = new URLSearchParams({ limit: "20" });
  if (cursor) { if (cursor.length > 512) throw new Error("community_owner_history_invalid"); params.set("cursor", cursor); }
  return parseOwnedCommunityPublications(await communityRequest(`/api/v2/strategy-publications/mine?${params}`, { method: "GET", signal }, locale, authToken, isCurrent));
}

export interface CommunityPublicationDraft {
  shareID: string;
  title: string;
  summary: string;
  creatorAlias: string;
  tags: readonly string[];
}

export interface GeneratedCommunityPublicationAttempt {
  readonly accountID: string;
  readonly identity: string;
  readonly idempotencyKey: string;
  readonly payload: Readonly<{ schema_version: 1; share_id: string; locale: string }>;
}

/** The server derives all public text and verifies the historical result. */
export function prepareGeneratedCommunityPublication(
  accountID: string,
  shareID: string,
  locale: string,
  previous?: GeneratedCommunityPublicationAttempt | null,
): GeneratedCommunityPublicationAttempt {
  if (!accountID || !SHARE_ID_PATTERN.test(shareID) || !/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,4})?$/.test(locale)) {
    throw new Error("community_publish_payload_invalid");
  }
  const payload = Object.freeze({ schema_version: 1 as const, share_id: shareID, locale });
  const identity = JSON.stringify([accountID, payload]);
  if (previous?.identity === identity) return previous;
  return Object.freeze({ accountID, identity, idempotencyKey: idempotencyKey("community-generated"), payload });
}

export async function publishGeneratedCommunityStrategy(
  attempt: GeneratedCommunityPublicationAttempt,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<CommunityPublication> {
  const value = record(await communityRequest(
    "/api/v2/strategy-publications/generated",
    {
      method: "POST", signal,
      headers: { "X-Idempotency-Key": attempt.idempotencyKey },
      body: JSON.stringify(attempt.payload),
    },
    attempt.payload.locale, authToken, isCurrent,
  ));
  const publication = parseCommunityPublicationForStatuses(value?.publication, PUBLISH_RESPONSE_STATUSES);
  if (!publication) throw new Error("community_publication_invalid");
  return publication;
}

export interface CommunityPublicationAttempt {
  readonly accountID: string;
  readonly identity: string;
  readonly idempotencyKey: string;
  readonly payload: Readonly<{
    schema_version: 1;
    share_id: string;
    title: string;
    summary: string;
    creator_alias: string;
    tags: readonly string[];
  }>;
}

/** Keep uncertain retries bound to the exact normalized payload and owner. */
export function prepareCommunityPublication(
  accountID: string,
  draft: CommunityPublicationDraft,
  previous?: CommunityPublicationAttempt | null,
): CommunityPublicationAttempt {
  const normalizedTitle = strictText(draft.title.trim(), 1, 80);
  const normalizedSummary = strictText(draft.summary.trim(), 1, 500);
  const normalizedAlias = strictText(draft.creatorAlias.trim(), 2, 40);
  const normalizedTags = strictTags(
    draft.tags.slice(0, 8).map((tag) => tag.trim()).filter(Boolean),
  );
  if (!accountID || !SHARE_ID_PATTERN.test(draft.shareID) || !normalizedTitle || !normalizedSummary || !normalizedAlias || !normalizedTags) {
    throw new Error("community_publish_payload_invalid");
  }
  const payload = Object.freeze({
    schema_version: 1 as const,
    share_id: draft.shareID,
    title: normalizedTitle,
    summary: normalizedSummary,
    creator_alias: normalizedAlias,
    tags: Object.freeze(normalizedTags),
  });
  const identity = JSON.stringify([accountID, payload]);
  if (previous?.identity === identity) return previous;
  return Object.freeze({
    accountID,
    identity,
    idempotencyKey: idempotencyKey("community-publish"),
    payload,
  });
}

export async function publishCommunityStrategy(
  attempt: CommunityPublicationAttempt,
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<CommunityPublication> {
  const value = await communityRequest(
    "/api/v2/strategy-publications",
    {
      method: "POST",
      signal,
      headers: { "X-Idempotency-Key": attempt.idempotencyKey },
      body: JSON.stringify(attempt.payload),
    },
    locale,
    authToken,
    isCurrent,
  );
  const envelope = record(value);
  const publication = parseCommunityPublicationForStatuses(
    envelope?.publication,
    PUBLISH_RESPONSE_STATUSES,
  );
  if (!publication) throw new Error("community_publication_invalid");
  return publication;
}
